import Vue from "vue";
import { timer } from "rxjs";
export { searchUniversal, suggestUniversalValues } from "./api.search";

const normalizePagedResponse = response => ({
  count: Number(response && response.count) || 0,
  next: (response && response.next) || null,
  previous: (response && response.previous) || null,
  results: Array.isArray(response && response.results) ? response.results : []
});

const chunkArray = (values, chunkSize) => {
  const chunks = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
};

// Public APIs
export const convertSeriesInstanceUUIDToDatabaseId = seriesUUID => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(`api/series/uuid-to-id/${seriesUUID}/`)
      .then(({ id }) => resolve(id))
      .catch(error => reject(error));
  });
};

export const launchSeriesDeletionJob = ids => {
  let data = {
    ids: ids
  };
  return Vue.$http.post(`api/tasks/series-delete/`, null, data);
};

export const launchPatientDeletionJob = id => {
  let data = {
    id: id
  };
  return Vue.$http.post(`api/tasks/patient-delete/`, null, data);
};

export const launchStudyDeletionJob = id => {
  let data = {
    id: id
  };
  return Vue.$http.post(`api/tasks/study-delete/`, null, data);
};

export const sharePatientOrStudy = (shareLevel, sharedObjectId) => {
  let data = {
    shareLevel,
    sharedObjectId
  };
  return Vue.$http.post(`api/create-shared-link/`, null, data);
};

export const generateUploadLink = () => {
  return Vue.$http.post(`api/create-upload-link/`, null, null);
};

export const getUserFeatures = () => {
  return Vue.$http.get("api/user-features/");
};

export const getQuotaUsage = () => {
  return Vue.$http.get("api/quota-usage/");
};

export const getBillingCatalog = () => {
  return Vue.$http.get("api/billing-catalog/");
};

export const submitEnterpriseInquiry = payload => {
  return Vue.$http.post("api/enterprise-inquiry/", null, payload);
};

export const sendLinkViaMail = (email, link) => {
  const data = {
    email: email,
    link: link
  };
  return new Promise((resolve, reject) => {
    Vue.$http
      .post(`api/send-link/`, null, data)
      .then(res => resolve(res))
      .catch(reject);
  });
};

export const getPatients = (orderBy, page, pageSize) => {
  return getPatientsPaged(orderBy, page, pageSize).then(({ results }) => results);
};

export const getPatientsPaged = (orderBy, page, pageSize) => {
  return new Promise((resolve, reject) => {
    const req = "api/patients/";
    let params;
    if (orderBy && page && pageSize) {
      params = {
        ordering: orderBy,
        page: page,
        page_size: pageSize
      };
    }
    Vue.$http
      .get(req, params)
      .then(response => resolve(normalizePagedResponse(response)))
      .catch(error => reject(error));
  });
};

export const getPredictors = () => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get("api/predictors")
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const getProjects = () => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get("api/projects")
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const getSeries = id => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(`api/series/${id}`)
      .then(seriesResponse => resolve({ ...seriesResponse }))
      .catch(reject);
  });
};

export const getStudySeries = studyId => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(`api/studies/${studyId}/series/`)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const getAllStudies = () => {
  return getStudiesPaged().then(({ results }) => results);
};

export const getStudiesPaged = (orderBy, page, pageSize) => {
  return new Promise((resolve, reject) => {
    const req = "api/studies/";
    const params = {};
    if (orderBy) params.ordering = orderBy;
    if (page) params.page = page;
    if (pageSize) params.page_size = pageSize;

    Vue.$http
      .get(req, params)
      .then(response => resolve(normalizePagedResponse(response)))
      .catch(error => reject(error));
  });
};

export const getStudiesByIds = (studyIds, orderBy = "updated_at") => {
  const uniqueStudyIds = [...new Set((studyIds || []).map(id => Number(id)))]
    .filter(Number.isFinite)
    .filter(id => id > 0);
  if (!uniqueStudyIds.length) {
    return Promise.resolve([]);
  }

  const idChunks = chunkArray(uniqueStudyIds, 80);
  const req = "api/studies/";

  return Promise.all(
    idChunks.map(idsChunk => {
      const params = {
        ordering: orderBy,
        id__in: idsChunk.join(","),
        page_size: idsChunk.length,
        page: 1
      };
      return Vue.$http
        .get(req, params)
        .then(response => normalizePagedResponse(response).results);
    })
  ).then(chunkResults => {
    const merged = chunkResults.flat();
    const dedupedById = new Map();
    merged.forEach(study => {
      if (study && study.id !== undefined && study.id !== null) {
        dedupedById.set(study.id, study);
      }
    });
    return Array.from(dedupedById.values());
  });
};

export const postStudy = (studyData, uploadId) => {
  return new Promise((resolve, reject) => {
    const payload = {
      studyData,
      uploadId
    };

    Vue.$http
      .post("api/studies/series", null, payload)
      .then(resp => resolve(resp.body.data))
      .catch(error => reject(error));
  });
};

export const postJob = data => {
  return new Promise((resolve, reject) => {
    console.log("create job", data);
    if (
      data.predictorData &&
      data.predictorData.command &&
      data.predictorData.command == "initialize_video"
    ) {
      // sam2 initialization: show sam2 panels
      // sam2 state = wait for points
    }
    Vue.$http
      .post("api/create_job", null, data)
      .then(resp => {
        resolve(resp.body);
      })
      .catch(error => reject(error));
  });
};

export { sendDicomFiles } from "@/js/upload";

export const pollAsyncTasksStatus = (onData, onError) => {
  // fetch every 60 seconds
  return timer(0, 60 * 1000).subscribe(() => {
    Vue.$http
      .get("api/tasks/running")
      .then(response => onData(response))
      .catch(error => onError(error));
  });
};

export const getUserSettings = () => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get("api/settings")
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const putUserSetting = (name, value) => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .put("api/settings", null, { [name]: value })
      .then(response => resolve(response.body))
      .catch(error => reject(error));
  });
};

export const getSharedStudy = shareId => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(`api/shared/${shareId}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => reject(error));
  });
};

export const postMasksCreate = data => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .post(`api/masks/`, null, data)
      .then(response => {
        resolve(response);
      })
      .catch(error => reject(error));
  });
};

export const postDeleteMasks = data => {
  return new Promise((resolve, reject) => {
    Vue.$http
      .post(`api/delete-masks/`, null, data)
      .then(response => {
        resolve(response);
      })
      .catch(error => reject(error));
  });
};

/**
 * Get studies with specific order and paging
 *
 */
export const getStudies = (orderBy, page, pageSize) => {
  return getStudiesPaged(orderBy, page, pageSize).then(({ results }) => results);
};

/**
 * Query studies
 */
export const queryStudies = (query, orderBy, page, pageSize) => {
  return queryStudiesPaged(query, orderBy, page, pageSize).then(
    ({ results }) => results
  );
};

export const queryStudiesPaged = (query, orderBy, page, pageSize) => {
  const {
    patientName,
    modality,
    studyDateGT,
    studyDateLT,
    accessionNumber
  } = query;
  const req = "api/studies/";
  const params = {
    ordering: orderBy,
    page: page,
    modality: modality,
    patient: patientName,
    study_date__gte: studyDateGT,
    study_date__lte: studyDateLT,
    accession_number: accessionNumber,
    page_size: pageSize
  };
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(req, params)
      .then(response => resolve(normalizePagedResponse(response)))
      .catch(error => reject(error));
  });
};

export const queryPatients = (query, orderBy, page, pageSize) => {
  return queryPatientsPaged(query, orderBy, page, pageSize).then(
    ({ results }) => results
  );
};

export const queryPatientsPaged = (query, orderBy, page, pageSize) => {
  if (!query && !orderBy && !page) {
    return getPatientsPaged(orderBy, page, pageSize);
  }
  const { patientName, birthDate, gender } = query;
  const req = "api/patients/";
  const params = {
    ordering: orderBy,
    page: page,
    gender: gender,
    patient: patientName,
    birth_date__gte: birthDate,
    page_size: pageSize
  };
  return new Promise((resolve, reject) => {
    Vue.$http
      .get(req, params)
      .then(response => resolve(normalizePagedResponse(response)))
      .catch(error => reject(error));
  });
};

export const getSeriesFiles = (seriesIDs, onProgress) => {
  let data = {
    ids: seriesIDs
  };
  const options = { responseType: "blob" };
  if (onProgress) {
    options.downloadProgress = onProgress;
  }
  return new Promise((resolve, reject) => {
    Vue.http
      .post(`api/download-series-files/`, data, options)
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const getDownloadProgress = () => {
  return Vue.http
    .get(`api/download-series-files/`)
    .then(response => response.json());
};

export const updatePatientCustomTag = patient => {
  return new Promise((resolve, reject) => {
    Vue.http
      .patch(`api/patient/${patient.id}`, {
        custom_tag: patient.custom_tag,
        user: patient.user
      })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const updateStudyCustomTag = study => {
  return new Promise((resolve, reject) => {
    Vue.http
      .patch(`api/study/${study.id}`, { custom_tag: study.custom_tag })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const createProject = (projectName, workgroup) => {
  return new Promise((resolve, reject) => {
    Vue.http
      .post("api/projects", { name: projectName, workgroups: [workgroup] })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const createLabel = (labelName, labelColor, projectId) => {
  return new Promise((resolve, reject) => {
    Vue.http
      .post("api/labels", {
        name: labelName,
        color: labelColor,
        project: projectId,
        predictor: null
      })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const updateStudyMedicalReport = reportObj => {
  return new Promise((resolve, reject) => {
    Vue.http
      .patch(`api/study/${reportObj.studyId}`, {
        medical_report: reportObj.content
      })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};

export const updateSeriesJsonArtifact = (artifactId, data) => {
  return new Promise((resolve, reject) => {
    Vue.http
      .patch(`api/artifacts/${artifactId}/`, { data })
      .then(response => resolve(response))
      .catch(error => reject(error));
  });
};
